import { defineStore } from 'pinia';

function parsePrivacyFilename(filename: string) {
    const m = filename.match(
        /^(\d{8})_SHAPTH_Datenschutzerklaerung_v(\d+)\.md$/i
    );
    if (!m) {
        return {
            key: filename,
            label: `Unknown (${filename})`,
            dateNum: 0,
            versionNum: 0
        };
    }
    const dateRaw = m[1];
    const vRaw = m[2];
    return {
        key: `${dateRaw}-v${vRaw}`,
        label: `v${vRaw}`,
        dateNum: Number(dateRaw),
        versionNum: Number(vRaw)
    };
}

function getLocalStorageKey(userId: string): string {
    return `privacy.accepted:${userId}`;
}

function resolveLatestPrivacy(): {
    url: string;
    filename: string;
    key: string;
    label: string;
} {
    const files = import.meta.glob('@/assets/*Datenschutzerklaerung*.md', {
        eager: true,
        as: 'url'
    }) as Record<string, string>;

    const entries = Object.entries(files).map(([virtualPath, url]) => {
        const filename =
            virtualPath.split('/').pop() ?? 'Datenschutzerklaerung.md';
        const p = parsePrivacyFilename(filename);
        return { url, filename, ...p };
    });

    if (entries.length === 0) {
        throw new Error(
            'no privacy found (assets/*Datenschutzerklaerung*.md).'
        );
    }

    entries.sort((a, b) => {
        if (a.dateNum !== b.dateNum) return b.dateNum - a.dateNum;
        return b.versionNum - a.versionNum;
    });

    const top = entries[0];
    return {
        url: top.url,
        filename: top.filename,
        key: top.key,
        label: top.label
    };
}

async function hashUserId(userId: string): Promise<string> {
    const data = new TextEncoder().encode(userId);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

export const usePrivacyStore = defineStore('privacy', () => {
    const isLoading = ref(false);

    const currentVersionId = ref<string | null>(null);
    const currentVersionLabel = ref<string | null>(null);
    const policyUrl = ref<string | null>(null);

    const acceptedVersionId = ref<string | null>(null);

    const session = useSessionStore();

    const storageKey = ref<string | null>(null);

    function getCurrentUserId(): string {
        const sc = (session as any)?.sessionContext ?? session;
        const fromContext: unknown = sc?.userId ?? sc?.sessionToken?.sub;
        return typeof fromContext === 'string' && fromContext
            ? fromContext
            : 'anonymous';
    }

    async function initStorageKey() {
        const userId = getCurrentUserId();
        const hashed = await hashUserId(userId);
        storageKey.value = getLocalStorageKey(hashed);

        return storageKey.value;
    }

    async function init() {
        if (isLoading.value) return;
        isLoading.value = true;
        try {
            const latest = resolveLatestPrivacy();
            policyUrl.value = latest.url;
            currentVersionId.value = latest.key;
            currentVersionLabel.value = latest.label;

            await initStorageKey();

            const raw =
                typeof localStorage !== 'undefined' && storageKey.value
                    ? localStorage.getItem(storageKey.value)
                    : null;

            if (!raw) {
                acceptedVersionId.value = null;
            } else {
                const obj = JSON.parse(raw) as {
                    key: string;
                    acceptedAt?: string;
                };
                acceptedVersionId.value = obj.key ?? null;
            }
        } catch (e) {
            console.error('privacy:init – initialization error', e);
            acceptedVersionId.value = null;
        } finally {
            isLoading.value = false;
        }
    }

    async function accept() {
        if (!currentVersionId.value) return;

        if (!storageKey.value) {
            await initStorageKey();
        }

        if (acceptedVersionId.value === currentVersionId.value) return;

        const value = JSON.stringify({
            key: currentVersionId.value,
            acceptedAt: new Date().toISOString()
        });
        try {
            if (storageKey.value) {
                localStorage.setItem(storageKey.value, value);
            }
            acceptedVersionId.value = currentVersionId.value;
        } catch (e) {
            console.warn('privacy:accept – persist failed', e);
        }
    }

    const needsReaccept = computed(() => {
        if (session.sessionContext.isAdminOrSupport) return false;
        if (!currentVersionId.value) return false;
        return acceptedVersionId.value !== currentVersionId.value;
    });

    const accepted = computed(
        () =>
            !!currentVersionId.value &&
            acceptedVersionId.value === currentVersionId.value
    );

    return {
        isLoading,
        policyUrl,
        currentVersionId,
        currentVersionLabel,
        acceptedVersionId,
        accepted,
        needsReaccept,
        init,
        accept
    };
});
